import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Collapse, Divider, Grid, IconButton } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import cn from 'classnames';
import { get } from 'lodash';
import { dateFormats, TP } from '../../../constants/consts';
import { useTranslation } from '../../../src/i18n';
import EditModeCard, { EditModeCardSection } from '../../../components/EditModeCard';
import CheckboxField from '../../../components/Fields/CheckboxField';
import { fontSize } from '../../../constants/styleConstants';
import ProductionCardCollapsedContent from '../../../components/ProductionCardCollapsedContent';
import { seaparateCastToRolesProfessions } from '../utils';
import TitledBlock from '../../../components/TitledBlock';
import { formatDateFromString } from '../../../utils/common';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    alignItems: 'center',
  },
  info: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
  },
  time: {
    fontSize: fontSize.large,
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
  expand: {
    padding: 2,
    marginRight: theme.spacing(2),
    cursor: 'pointer',
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    display: 'inline-block',
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  castInfo: {
    padding: theme.spacing(2),
  },
}));

export const performanceInfo = [
  {
    value: 'country.name',
    title: `${TP}.m_COUNTRY`,
  },
  {
    value: 'city.name',
    title: `${TP}.m_CITY`,
  },
  {
    value: 'venue.name',
    title: `${TP}.m_VENUE`,
  },
  {
    value: 'venueStage.name',
    title: `${TP}.m_ROOM`,
  },
];

const TimeSelectCard = ({ performanceTimeInfo, onSelect, className, isSelected }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);

  const getSeparateCast = React.useCallback(() => seaparateCastToRolesProfessions(performanceTimeInfo?.casts), [
    performanceTimeInfo,
  ]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <EditModeCard className={className}>
      <EditModeCardSection first className={classes.content}>
        <IconButton
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
          className={cn(classes.expand, {
            [classes.expandOpen]: expanded,
          })}
        >
          <ExpandMoreIcon className={classes.moreIcon} />
        </IconButton>
        <Grid container spacing={2}>
          <Grid item className={classes.time}>
            {formatDateFromString(
              performanceTimeInfo?.date,
              `DD ${dateFormats.productionMonth} ${dateFormats.dayOfWeekShort}`,
            )}
            {performanceTimeInfo?.time && ` - ${performanceTimeInfo?.time}`}
          </Grid>
          <div className={classes.info}>
            {performanceInfo.map(info => (
              <TitledBlock key={info.value} title={t(info.title)}>
                {get(performanceTimeInfo, info.value)}
              </TitledBlock>
            ))}
          </div>
        </Grid>
        <CheckboxField
          checked={isSelected}
          instanceId={`${performanceTimeInfo?.id}-select`}
          onChange={onSelect}
          name="select-date"
        />
      </EditModeCardSection>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider className={classes.gridDivider} />
        <ProductionCardCollapsedContent
          cast={getSeparateCast()?.cast}
          crew={getSeparateCast()?.crew}
          other={getSeparateCast()?.other}
          showEmpty
          showText
          className={classes.castInfo}
        />
      </Collapse>
    </EditModeCard>
  );
};

TimeSelectCard.propTypes = {
  performanceTimeInfo: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  className: PropTypes.string,
  isSelected: PropTypes.bool,
};

TimeSelectCard.defaultProps = {
  className: '',
  isSelected: false,
};

export default TimeSelectCard;
